import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Task, TaskColumn } from "@/features/tasks/types/task";
import { KanbanColumn } from "@/features/tasks/components/KanbanColumn";
import { TaskCard } from "@/features/tasks/components/TaskCard";

interface KanbanBoardProps {
  columns: TaskColumn[];
  tasks: Task[];
  onMoveTask: (taskId: string, columnId: string, order: number) => void;
  onAddTask: (columnId: string) => void;
  onSelectTask: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onAddColumn: () => void;
  onEditColumn: (column: TaskColumn) => void;
  onDeleteColumn: (columnId: string) => void;
  onCompleteTask: (task: Task) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  columns,
  tasks,
  onMoveTask,
  onAddTask,
  onSelectTask,
  onEditTask,
  onAddColumn,
  onEditColumn,
  onDeleteColumn,
  onCompleteTask,
}) => {
  const { t } = useTranslation();
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const getTasksForColumn = (columnId: string) =>
    tasks
      .filter((t) => t.column_id === columnId)
      .sort((a, b) => a.col_order - b.col_order);

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const overId = over.id as string;

    // Find source task
    const sourceTask = tasks.find((t) => t.id === taskId);
    if (!sourceTask) return;

    // Determine target column
    // overId could be a column id or a task id
    const isOverColumn = columns.some((c) => c.id === overId);
    const targetColumnId = isOverColumn
      ? overId
      : (tasks.find((t) => t.id === overId)?.column_id ?? sourceTask.column_id ?? "");

    if (!targetColumnId) return;

    const columnTasks = getTasksForColumn(targetColumnId);

    let newOrder: number;
    if (isOverColumn) {
      // Dropped directly on column → put at end
      newOrder = columnTasks.length;
    } else {
      // Dropped on another task → insert before it
      const overTaskIndex = columnTasks.findIndex((t) => t.id === overId);
      newOrder = overTaskIndex >= 0 ? overTaskIndex : columnTasks.length;
    }

    onMoveTask(taskId, targetColumnId, newOrder);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[calc(100vh-280px)]">
        {columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            tasks={getTasksForColumn(column.id)}
            onAddTask={onAddTask}
            onSelectTask={onSelectTask}
            onEditTask={onEditTask}
            onEditColumn={onEditColumn}
            onDeleteColumn={onDeleteColumn}
            onCompleteTask={onCompleteTask}
          />
        ))}

        {/* Add new column button */}
        <div className="flex items-start">
          <button
            onClick={onAddColumn}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-500 hover:text-indigo-600 border-2 border-dashed border-gray-300 hover:border-indigo-400 rounded-xl transition-colors whitespace-nowrap min-w-[180px] justify-center bg-white/50"
          >
            <Plus className="w-4 h-4" />
            {t("tasks.new_column")}
          </button>
        </div>
      </div>

      <DragOverlay>
        {activeTask && (
          <div className="rotate-2 opacity-90">
            <TaskCard
              task={activeTask}
              onSelect={() => {}}
              onEdit={() => {}}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
